import { collection, getDocs, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { merchProducts as staticProducts } from "../data/merchProducts";

export async function getMerchProducts({ publishedOnly = false } = {}) {
  let overrides = new Map();

  try {
    const snapshot = await getDocs(collection(db, "merchProducts"));
    overrides = new Map(snapshot.docs.map((item) => [item.id, item.data()]));
  } catch (error) {
    // Keep the existing bundled catalog available until the merch rules are deployed,
    // or whenever the optional remote mockup data cannot be reached.
    if (process.env.NODE_ENV === "development") {
      console.warn("[Merch Engine] Remote mockups unavailable; using bundled products.", error);
    }
  }

  const products = staticProducts.map((product) => {
    const override = overrides.get(product.id) || {};
    return {
      ...product,
      status: ["draft", "published", "archived"].includes(override.status)
        ? override.status
        : product.status,
      mockupUrl: typeof override.mockupUrl === "string"
        ? override.mockupUrl
        : product.mockupUrl,
      alternateMockupUrls: Array.isArray(override.alternateMockupUrls)
        ? override.alternateMockupUrls.filter((url) => typeof url === "string")
        : product.alternateMockupUrls,
      mockupVariants: Array.isArray(override.mockupVariants)
        ? override.mockupVariants.filter((variant) => (
          typeof variant?.url === "string"
          && typeof variant?.color === "string"
          && ["front", "back"].includes(variant?.view)
        ))
        : []
    };
  });
  return publishedOnly ? products.filter((product) => product.status === "published") : products;
}

export async function publishMerchMockupSet({ productId, mockups }) {
  if (!productId || !staticProducts.some((product) => product.id === productId)) {
    throw new Error("Select a valid merch product.");
  }
  if (!Array.isArray(mockups) || mockups.length === 0) {
    throw new Error("Prepare a garment mockup set before publishing.");
  }

  const timestamp = Date.now();
  const uploaded = [];
  for (const [index, mockup] of mockups.entries()) {
    const file = mockup.file;
    if (!file || !file.type?.startsWith("image/")) throw new Error("A garment mockup image is invalid.");
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
    const storagePath = `merchMockups/${productId}/${timestamp}-${index + 1}-${safeName}`;
    const mockupRef = ref(storage, storagePath);
    await uploadBytes(mockupRef, file, { contentType: file.type });
    uploaded.push({
      url: await getDownloadURL(mockupRef),
      storagePath,
      color: mockup.color,
      view: mockup.view
    });
  }

  await setDoc(doc(db, "merchProducts", productId), {
    mockupUrl: uploaded[0].url,
    mockupStoragePath: uploaded[0].storagePath,
    alternateMockupUrls: uploaded.slice(1).map((mockup) => mockup.url),
    mockupVariants: uploaded.map(({ url, color, view }) => ({ url, color, view })),
    status: "published",
    updatedAt: serverTimestamp()
  }, { merge: true });

  return uploaded;
}

export async function publishMerchMockup({ productId, file }) {
  if (!productId) throw new Error("Select a product before publishing.");
  if (!staticProducts.some((product) => product.id === productId)) throw new Error("Select a valid merch product.");
  if (!file || !file.type?.startsWith("image/")) throw new Error("Select a valid garment mockup image.");

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const storagePath = `merchMockups/${productId}/${Date.now()}-${safeName}`;
  const mockupRef = ref(storage, storagePath);
  await uploadBytes(mockupRef, file, { contentType: file.type });
  const mockupUrl = await getDownloadURL(mockupRef);

  await setDoc(doc(db, "merchProducts", productId), {
    mockupUrl,
    mockupStoragePath: storagePath,
    status: "published",
    updatedAt: serverTimestamp()
  }, { merge: true });

  return mockupUrl;
}
